import { useEffect, useState } from "react";
import { X, Check, Loader2, ShieldCheck, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * PickPlanModal — Free / Growth / Master / Broker.
 *
 * Pricing + client caps come from `GET /billing/plans` so the copy stays
 * in lockstep with the enforcement helpers on the backend. If the agent
 * has already outgrown their current tier (client_count > client_limit)
 * we pre-select the tier that fits them so the "why is my form blocked?"
 * moment resolves in a single click.
 */
export default function PickPlanModal({ onClose, defaultTier }) {
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(defaultTier || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/plans")
      .then(r => {
        setPayload(r.data);
        // Preselect: caller wins, then the server's `suggested_tier`
        // (needed-to-fit-current-book), then Growth as the sensible entry.
        setSelected(prev => prev
          || r.data?.current?.suggested_tier
          || "growth");
      })
      .catch(() => toast.error("Could not load plan pricing"));
  }, []);

  const confirm = async () => {
    if (!selected) return;
    if (selected === "free") {
      onClose?.();
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan: selected,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="pick-plan-modal">
      <div className="w-full max-w-5xl rounded-xl bg-white shadow-2xl my-8">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Subscribe</div>
            <h3 className="font-heading text-2xl font-bold tracking-tighter text-slate-900">Pick your plan</h3>
            <p className="mt-1 text-sm text-slate-500">Priced by client volume — upgrade or downgrade any time from Settings.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="pick-plan-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!payload ? (
          <div className="p-16 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
          </div>
        ) : (
          <>
            {payload.current?.client_limit_reached && (
              <div className="mx-5 mt-5 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 flex items-center gap-2 text-xs text-rose-900" data-testid="pick-plan-limit-banner">
                <AlertTriangle className="h-4 w-4 shrink-0 text-rose-700" />
                <span>
                  <b>Limit reached.</b> You're at your {payload.current.tier_name} plan's cap
                  ({payload.current.client_count}/{payload.current.client_limit} clients).
                  Upgrade to <b>{tierNameOf(payload.tiers, payload.current.suggested_tier)}</b> to keep adding clients.
                </span>
              </div>
            )}

            {/* DocuSign parity call-out — every paid tier ships it, so we
                surface that once, front-and-center, instead of relying on
                the feature bullet list per card to carry the message. */}
            <div className="mx-5 mt-5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2 text-xs text-emerald-900" data-testid="pick-plan-docusign-note">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-700" />
              <span><b>DocuSign e-signatures included on every paid plan</b> — Growth, Master, and Pro. Free is CRM-only.</span>
            </div>

            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {payload.tiers.map(t => (
                <TierCard
                  key={t.key}
                  tier={t}
                  selected={selected === t.key}
                  current={payload.current?.tier === t.key}
                  recommended={payload.current?.suggested_tier === t.key && !payload.current?.exempt}
                  onSelect={() => setSelected(t.key)}
                />
              ))}
            </div>

            {/* Brokerage / multi-agent teams live outside the self-serve
                ladder — one contact-sales row directs them to us rather
                than making them try to shoehorn 30+ agents into Pro. */}
            <div className="mx-5 mb-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid="pick-plan-brokerage-note">
              <div className="text-xs text-slate-700">
                <div className="font-semibold text-slate-900">Running a brokerage or a multi-agent team?</div>
                <div className="text-slate-600 mt-0.5">Brokerage pricing (multi-seat, roster billing, admin roles) is quoted separately — we'll tailor it to your team size.</div>
              </div>
              <a
                href="mailto:sales@ikey.app?subject=Brokerage%20pricing%20inquiry"
                className="text-xs font-semibold underline underline-offset-4 hover:opacity-80 whitespace-nowrap"
                style={{ color: "var(--primary-hex)" }}
                data-testid="pick-plan-brokerage-contact"
              >
                Contact us for brokerage pricing →
              </a>
            </div>

            <div className="mx-5 mb-3 rounded-md bg-slate-50 border border-slate-200 p-3 flex items-start gap-2 text-xs text-slate-600" data-testid="pick-plan-guarantee">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--primary-hex)" }} />
              <div>
                <span className="font-semibold text-slate-900">Fair cancellation guarantee.</span>{" "}
                Cancel any time from Settings — you keep full CRM and client-portal access through the end of your current billing cycle. We never revoke early.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-100">
              <button onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid="pick-plan-cancel">Not yet</button>
              <button
                onClick={confirm}
                disabled={busy || !selected}
                className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "var(--primary-hex)" }}
                data-testid="pick-plan-confirm"
              >
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>
                  : selected === "free" ? <>Stay on Free</>
                  : <>Continue to checkout</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TierCard({ tier, selected, current, recommended, onSelect }) {
  const isFree = tier.key === "free";
  const price = isFree ? "$0" : `$${tier.price_usd.toFixed(2)}`;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-lg border-2 p-5 transition-all hover:shadow ${selected ? "bg-white" : "border-slate-200 bg-white"}`}
      style={selected ? { borderColor: "var(--primary-hex)" } : {}}
      data-testid={`plan-${tier.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">{tier.name}</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-600 mt-0.5 font-semibold">{tier.client_limit_label}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {current && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-800 bg-slate-100 px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
          {recommended && !current && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-white px-2 py-0.5 rounded-full" style={{ background: "var(--primary-hex)" }}>
              Recommended
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-baseline gap-1.5">
        <div className="font-heading text-3xl font-bold tabular-nums text-slate-900">{price}</div>
        <div className="text-sm text-slate-600">{isFree ? "forever" : "per month"}</div>
      </div>
      <p className="text-xs text-slate-600 mt-1 min-h-[32px]">{tier.tagline}</p>

      {/* DocuSign chip — greens for included, muted-red for locked. Keeps
          the parity message readable at a glance without needing to scan
          the bullet list. */}
      <div className="mt-3">
        {tier.docusign ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <Check className="h-3 w-3" /> DocuSign included
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-rose-800 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">
            <Lock className="h-3 w-3" /> DocuSign not included
          </span>
        )}
      </div>

      <ul className="mt-3 space-y-1.5">
        {tier.features.map(f => {
          const locked = f.toLowerCase().includes("not included") || f.toLowerCase().includes("locked");
          const Icon = locked ? Lock : Check;
          return (
            <li key={f} className={`flex items-start gap-2 text-sm ${locked ? "text-slate-500" : "text-slate-800"}`}>
              <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" style={locked ? {} : { color: "var(--primary-hex)" }} /> {f}
            </li>
          );
        })}
      </ul>

      <div className={`mt-4 text-[11px] font-semibold uppercase tracking-widest ${selected ? "" : "text-slate-500"}`} style={selected ? { color: "var(--primary-hex)" } : {}}>
        {selected ? "Selected" : "Choose"}
      </div>
    </button>
  );
}

function titleCase(k) {
  if (!k) return "";
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function tierNameOf(tiers, key) {
  const t = tiers?.find(x => x.key === key);
  return t?.name || titleCase(key);
}
